import Resource, { ResourceApi } from '../../resource/Resource';
import { USER_RESOURCE, ENROLMENT, COURSE } from '../resourceNames';

import {
  SET_ENROLMENT_DISPLAYED,
  LEAVE_COHORTS,
  COURSE_ENROLMENT_START,
  COURSE_ENROLMENT_FAILURE,
  DISMISS_COURSE_ENROLMENT_ERROR_MODAL,
  COURSE_EXTERNAL_ENROLMENT_START,
  COURSE_PAID_ENROLMENT_START,
  COURSE_ACCESS_CODE_ENROLMENT_START,
  COURSE_ACCESS_CODE_ENROLMENT_CANCEL,
  COURSE_PRE_ENROLMENT_QUESTIONS_ENROLMENT_START,
  COURSE_ENROLMENT_CLASS_SELECTION_START,
  COURSE_ENROLMENT_CLASS_SELECTION_CANCEL,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_START,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_SUCCESS,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
  COURSE_ENROLMENT_ACCESS_CODE_CHANGE,
  COURSE_ENROLMENT_CLASS_SELECTION_CHANGE,
} from './actions';
import { selectLocation } from '../location/selectors';
import { currentUserIdSelector, selectCsrfToken } from '../Auth/selectors';
import { getActionForUrl } from '../../web/utilities/reduxLinks';
import { GTM } from '../../web/metrics/google-tag-manager';
import { noop } from '../../web/utilities/functions';
import { makeSelectCourseAllowsPrivateClassSelection } from '../CoursePromoData/selectors';
import {
  selectEnrolmentMode,
  selectClassOption,
  selectCourseForEnrolment,
  selectAccessCode,
} from './EnrolmentModal/selectors';
import { ENROLMENT_MODAL_MODE_CLASS_SELECTION } from './EnrolmentModal/constants';

export { setEnrolments } from './dispatchers.common';

// change enrolment setting to show/hide from topbar menu
export const setEnrolmentDisplayed = (enrolmentId, display) => {
  return {
    type: SET_ENROLMENT_DISPLAYED,
    enrolmentId,
    display,
  };
};

// unenrol from multiple cohorts
export const deleteEnrolments = (enrolmentIds) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      ResourceApi.execute('enrolment', 'unenrolFromCohorts', {
        enrolmentIds,
      }).then((response) => {
        const { success } = response;

        dispatch({
          type: LEAVE_COHORTS,
          enrolmentIds: success,
        });

        resolve(response);
      }, reject);
    });
  };
};

export const changeUserCohort = (cohortPath) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const currentUserId = currentUserIdSelector(getState());
      Resource.update(USER_RESOURCE, currentUserId, 'changeCohort', {
        cohortPath,
      }).then(() => {
        let location = selectLocation(getState());
        if (location.hasIn(['query', 'inCohort'])) {
          location = location.setIn(['query', 'inCohort'], cohortPath);
          dispatch(location.toJS());
        } else {
          // eslint-disable-next-line no-console
          console.log('enrolments.changeUserCohort(): reload');
          window.location.reload();
        }
        resolve();
      }, reject);
    });
  };
};

const redirectToSubscriptionPaymentUrl = (course, subscriptionPaymentUrl) => {
  const courseId = course.get('id');
  const action = getActionForUrl(subscriptionPaymentUrl);
  return { ...action, query: { courseId } };
};

const doExternalEnrolment = (externalEnrolmentLink) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_EXTERNAL_ENROLMENT_START,
      externalEnrolmentLink,
    });

    window.setTimeout(() => {
      window.location.href = externalEnrolmentLink;
    }, 3000);
  };
};

const doPaidEnrolment = (paymentCreationData) => {
  return (dispatch, getState) => {
    dispatch({ type: COURSE_PAID_ENROLMENT_START });

    const csrfToken = selectCsrfToken(getState());

    // create payment and redirect to payment details page
    ResourceApi.create('payment', paymentCreationData, ['resource'])
      .then((response) => {
        const paymentTransaction = response[0];
        const form = document.createElement('form');
        form.action = '/payment/details/';
        form.method = 'post';

        const formData = [
          { name: 'csrfmiddlewaretoken', value: csrfToken },
          { name: 'payment_transaction_id', value: paymentTransaction.id },
        ];

        formData.forEach(({ name, value }) => {
          const input = document.createElement('input');
          input.value = value;
          input.name = name;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      })
      .then(noop, (error) => {
        dispatch({
          type: COURSE_ENROLMENT_FAILURE,
          error: {
            title: 'Error creating payment',
            message: error.response.reason,
          },
        });
      });
  };
};

const doAccessCodeEnrolment = {
  type: COURSE_ACCESS_CODE_ENROLMENT_START,
};

export function cancelAccessCodeEnrolment() {
  return (dispatch) => {
    dispatch({ type: COURSE_ACCESS_CODE_ENROLMENT_CANCEL });
  };
}

const doPreEnrolmentQuestionsEnrolment = (course, accessCode, cohortName) => {
  return (dispatch, getState) => {
    dispatch({
      type: COURSE_PRE_ENROLMENT_QUESTIONS_ENROLMENT_START,
      course: course.get('path'),
      accessCode,
      cohortName,
    });

    const csrfToken = selectCsrfToken(getState());
    const form = document.createElement('form');
    form.action = '/courses/enrol/';
    form.method = 'post';

    const formData = [
      { name: 'csrfmiddlewaretoken', value: csrfToken },
      { name: 'course', value: course.get('path') },
      { name: 'enrol', value: 1 },
      { name: 'activationCode', value: accessCode },
      { name: 'cohortName', value: cohortName },
    ];

    formData.forEach(({ name, value }) => {
      const input = document.createElement('input');
      input.value = value;
      input.name = name;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };
};

const createEnrolment = ({ course, accessCode, paymentId }) => {
  return (dispatch) => {
    return ResourceApi.create(ENROLMENT, {
      course: course.get('path'),
      paymentId,
      accessCode,
    }).then(
      () => {
        if (!paymentId) {
          // just pushing to GTM here, instead of GTM and Segment
          GTM.pushEvent('GACourse-EnrolledForFree', {
            productID: `COURSE_${course.get('id')}`,
            priceAUD: 0,
          });
        }
        window.location.href = course.get('url');
      },
      () => {
        dispatch({
          type: COURSE_ENROLMENT_FAILURE,
          // enrolment modal has a default error message
        });
      }
    );
  };
};

export const doClassSelectionEnrolment = {
  type: COURSE_ENROLMENT_CLASS_SELECTION_START,
};

export const cancelClassSelectionEnrolment = {
  type: COURSE_ENROLMENT_CLASS_SELECTION_CANCEL,
};

export const updateCourseEnrolmentClassSelectionOption = (classOption) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_ENROLMENT_CLASS_SELECTION_CHANGE,
      classOption,
    });
  };
};

export const updateCourseEnrolmentAccessCode = (accessCode) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_ENROLMENT_ACCESS_CODE_CHANGE,
      accessCode,
    });
  };
};

export const checkAccessCode = (course, accessCode) => {
  return (dispatch) => {
    dispatch({
      type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_START,
      course: course.get('path'),
      accessCode,
    });

    return ResourceApi.read(
      COURSE,
      course.get('path'),
      'getCohortByAccessCode',
      {
        accessCode,
      }
    ).then(
      (result) => {
        if (result) {
          dispatch({
            type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_SUCCESS,
            accessCode,
            cohort: result,
          });
        } else {
          dispatch({
            type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
            error:
              'The access code is invalid. Please check the access code you have entered and try again.',
          });
        }
      },
      (error) => {
        dispatch({
          type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
          error: error.response.message,
        });
      }
    );
  };
};

export const enrolInCourse = (
  course,
  accessCode,
  onAccessCodeFailureCallback
) => {
  return (dispatch, getState) => {
    dispatch({
      type: COURSE_ENROLMENT_START,
      course: course.get('path'),
      accessCode,
    });

    const state = getState();
    const mode = selectEnrolmentMode(getState());
    const shouldAllowClassSelection = makeSelectCourseAllowsPrivateClassSelection(
      course.get('path')
    )(state);

    if (
      shouldAllowClassSelection &&
      mode !== ENROLMENT_MODAL_MODE_CLASS_SELECTION
    ) {
      dispatch(doClassSelectionEnrolment);
      return Promise.resolve();
    }

    return ResourceApi.lookup('course', 'checkEnrolment', {
      course: course.get('path'),
      accessCode,
    }).then(
      (result) => {
        const { enrolled, enrolmentMethod } = result;
        if (enrolled) {
          window.location.href = course.get('url');
        } else if (enrolmentMethod === 'subscriptionPayment') {
          const { subscriptionPaymentUrl } = result;
          dispatch(
            redirectToSubscriptionPaymentUrl(course, subscriptionPaymentUrl)
          );
        } else if (enrolmentMethod === 'externalEnrolment') {
          const { externalEnrolmentLink } = result;
          dispatch(doExternalEnrolment(externalEnrolmentLink));
        } else if (enrolmentMethod === 'paidEnrolment') {
          const { paymentId, paymentCreationData } = result;
          if (paymentId) {
            dispatch(createEnrolment({ course, paymentId }));
          } else {
            dispatch(doPaidEnrolment(paymentCreationData));
          }
        } else if (enrolmentMethod === 'requiresAccessCode') {
          dispatch(doAccessCodeEnrolment);
        } else if (enrolmentMethod === 'requiresPreEnrolmentQuestions') {
          const { cohortName } = result;
          dispatch(
            doPreEnrolmentQuestionsEnrolment(course, accessCode, cohortName)
          );
        } else {
          dispatch(createEnrolment({ course, accessCode }));
        }
      },
      (error) => {
        if (accessCode) {
          if (onAccessCodeFailureCallback) {
            onAccessCodeFailureCallback(error.response.reason);
          } else {
            dispatch({
              type: COURSE_ENROLMENT_FAILURE,
              error: {
                title: 'Invalid Access Code',
                message: error.response.reason,
              },
            });
          }
        } else if (error.response.error === 'AccessDenied') {
          dispatch({
            type: COURSE_ENROLMENT_FAILURE,
            error: {
              title: 'Access restricted',
              message: error.response.message,
            },
          });
        } else {
          dispatch({
            type: COURSE_ENROLMENT_FAILURE,
          });
        }
      }
    );
  };
};

export const dismissCourseEnrolmentErrorModal = {
  type: DISMISS_COURSE_ENROLMENT_ERROR_MODAL,
};

export const onSubmitClassSelectionModal = () => {
  return (dispatch, getState) => {
    const state = getState();
    const course = selectCourseForEnrolment(state);
    const classOption = selectClassOption(state);

    if (classOption === 'accessCode') {
      const accessCode = selectAccessCode(state);
      return dispatch(enrolInCourse(course, accessCode));
    }
    return dispatch(enrolInCourse(course));
  };
};
